import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { FileText, Plus, Loader2, Edit, Trash2, ArrowRight, Calendar, Users, MapPin, Search, Clock, Utensils, Table2, Settings2, Eye, Minus } from 'lucide-react';
import { useStandaloneBEOForm, napkinColors, tableclothColors, tableStyles } from '../hooks/useStandaloneBEOForm';
import { CustomMenuTiers } from '../components/CustomMenuTiers';

const StandaloneBEOs = () => {
  const bf = useStandaloneBEOForm();

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen" data-testid="standalone-beos-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Standalone BEOs</h1>
          <p className="text-gray-600">Create and manage Banquet Event Orders without contracts</p>
        </div>
        <Button onClick={() => bf.handleOpenDialog()} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="create-beo-btn">
          <Plus size={20} className="mr-2" />New BEO
        </Button>
      </div>

      <Card className="border-none shadow-lg mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search by event name, client, or BEO number..." value={bf.searchTerm} onChange={(e) => bf.setSearchTerm(e.target.value)} className="pl-10" data-testid="search-beos" />
          </div>
        </CardContent>
      </Card>

      {bf.loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#1e3a5f]" size={48} /></div>
      ) : bf.filteredBeos.length === 0 ? (
        <Card className="border-none shadow-lg">
          <CardContent className="p-12 text-center">
            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No BEOs found. Create your first standalone BEO!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bf.filteredBeos.map(beo => (
            <BEOCard key={beo.id} beo={beo} bf={bf} />
          ))}
        </div>
      )}

      <BEOFormDialog bf={bf} />
    </div>
  );
};

const BEOCard = ({ beo, bf }) => (
  <Card className="border-none shadow-lg hover:shadow-xl transition-shadow" data-testid={`beo-card-${beo.id}`}>
    <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-t-lg p-4">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-xs text-purple-200">{beo.beo_number}</p>
          <CardTitle className="text-lg">{beo.event_name}</CardTitle>
        </div>
        <span className={`px-2 py-1 rounded text-xs font-medium ${bf.getStatusColor(beo.status)}`}>{beo.status}</span>
      </div>
    </CardHeader>
    <CardContent className="p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <Users size={16} className="text-gray-400" />
        <span className="text-[#1e3a5f] font-medium">{beo.client_name}</span>
        {beo.secondary_client_name && <span className="text-gray-500">& {beo.secondary_client_name}</span>}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Calendar size={16} className="text-gray-400" />
        <span>{beo.event_date} at {beo.event_time}</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <MapPin size={16} className="text-gray-400" />
        <span className="truncate">{beo.location}</span>
      </div>
      <div className="text-sm text-gray-500">{beo.guest_count} guests - {beo.event_type}</div>
      {beo.contract_id && (
        <div className="pb-2 flex items-center gap-2 flex-wrap">
          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Converted to Contract</span>
          <button
            type="button"
            onClick={() => bf.handleOpenConvertedContract(beo)}
            className="text-xs text-[#1e3a5f] hover:underline font-semibold inline-flex items-center gap-1"
            data-testid={`open-contract-${beo.id}`}
          >
            Open Contract <ArrowRight size={12} />
          </button>
        </div>
      )}
      <div className="flex gap-2 pt-2 border-t">
        <Button variant="outline" size="sm" onClick={() => bf.navigate('/beo', { state: { beo } })} className="text-purple-600 hover:bg-purple-50" data-testid={`view-beo-${beo.id}`}>
          <Eye size={14} className="mr-1" />View
        </Button>
        {!beo.contract_id && (
          <Button variant="outline" size="sm" onClick={() => bf.handleOpenDialog(beo)} className="flex-1">
            <Edit size={14} className="mr-1" />Edit
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => bf.handleDelete(beo)} className="text-red-600 hover:bg-red-50" data-testid={`delete-beo-${beo.id}`}>
          <Trash2 size={14} />
        </Button>
        {!beo.contract_id && (
          <Button size="sm" onClick={() => bf.handleConvertToContract(beo)} className="bg-[#c9a227] hover:bg-[#b8922d] text-white" data-testid={`convert-beo-${beo.id}`}>
            <ArrowRight size={14} className="mr-1" />Contract
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);

const BEOFormDialog = ({ bf }) => (
  <Dialog open={bf.showDialog} onOpenChange={bf.setShowDialog}>
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-[#1e3a5f]">{bf.editingBeo ? 'Edit BEO' : 'Create Standalone BEO'}</DialogTitle>
        <DialogDescription>{bf.editingBeo ? 'Update the BEO details below.' : 'Enter event details to create a new BEO without a contract.'}</DialogDescription>
      </DialogHeader>
      <div className="space-y-6 py-4">
        <ClientSection bf={bf} />
        <EventDetailsSection bf={bf} />
        <EventTimelineSection bf={bf} />
        <MenuSelectionSection bf={bf} />
        {bf.formData.service_style === 'plated' && <PlatedMenuSection bf={bf} />}
        {bf.formData.event_type === 'cocktail_coach' && <CocktailCoachSection bf={bf} />}
        <TableLinenSection bf={bf} />
        <StationsSection bf={bf} />
        <NotesSection bf={bf} />
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={() => bf.setShowDialog(false)}>Cancel</Button>
        <Button onClick={bf.handleSave} disabled={bf.saving} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="save-beo-btn">
          {bf.saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
          {bf.editingBeo ? 'Update BEO' : 'Create BEO'}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

const ClientSection = ({ bf }) => (
  <>
    <div className="bg-gray-50 p-4 rounded-lg space-y-3">
      <Label className="font-medium text-[#1e3a5f]">Select Existing Client (Optional)</Label>
      <Select onValueChange={bf.handleClientSelect}>
        <SelectTrigger data-testid="client-select"><SelectValue placeholder="Choose a client to auto-fill..." /></SelectTrigger>
        <SelectContent>
          {bf.clients.map(client => (<SelectItem key={client.id} value={client.id}>{client.name} - {client.email}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
    <div className="border rounded-lg p-4">
      <h3 className="font-semibold text-[#1e3a5f] mb-4 flex items-center gap-2"><Users size={18} /> Client Information</h3>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Client Name *</Label><Input value={bf.formData.client_name} onChange={(e) => bf.setFormData({ ...bf.formData, client_name: e.target.value })} data-testid="beo-client-name" /></div>
        <div><Label>Secondary Client (for weddings)</Label><Input value={bf.formData.secondary_client_name} onChange={(e) => bf.setFormData({ ...bf.formData, secondary_client_name: e.target.value })} placeholder="e.g., Groom's family contact" data-testid="beo-secondary-client" /></div>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div><Label>Client Email</Label><Input type="email" value={bf.formData.client_email} onChange={(e) => bf.setFormData({ ...bf.formData, client_email: e.target.value })} /></div>
        <div><Label>Client Phone</Label><Input value={bf.formData.client_phone} onChange={(e) => bf.setFormData({ ...bf.formData, client_phone: e.target.value })} /></div>
      </div>
    </div>
  </>
);

const EventDetailsSection = ({ bf }) => (
  <div className="border rounded-lg p-4">
    <h3 className="font-semibold text-[#1e3a5f] mb-4 flex items-center gap-2"><Calendar size={18} /> Event Details</h3>
    <div className="grid grid-cols-2 gap-4">
      <div><Label>Event Name *</Label><Input value={bf.formData.event_name} onChange={(e) => bf.setFormData({ ...bf.formData, event_name: e.target.value })} data-testid="beo-event-name" /></div>
      <div>
        <Label>Event Type *</Label>
        <Select value={bf.formData.event_type} onValueChange={(v) => bf.setFormData({ ...bf.formData, event_type: v })}>
          <SelectTrigger data-testid="beo-event-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="onsite">Onsite</SelectItem><SelectItem value="offsite">Offsite</SelectItem>
            <SelectItem value="tournament">Tournament</SelectItem><SelectItem value="cocktail_coach">Cocktail Coach</SelectItem>
            <SelectItem value="pickup">Catering Pick Up</SelectItem><SelectItem value="dropoff">Catering Drop Off</SelectItem>
            <SelectItem value="celebration_of_life">Celebration of Life</SelectItem><SelectItem value="bridal_shower">Bridal Shower</SelectItem>
            <SelectItem value="baby_shower">Baby Shower</SelectItem><SelectItem value="birthday">Birthday</SelectItem>
            <SelectItem value="rehearsal_dinner">Rehearsal Dinner</SelectItem>
            <SelectItem value="golf_tournament">Golf Tournament</SelectItem>
            <SelectItem value="rustic_ranch">Rustic Ranch</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4 mt-4">
      <div><Label>Event Date *</Label><Input type="date" value={bf.formData.event_date} onChange={(e) => bf.setFormData({ ...bf.formData, event_date: e.target.value })} data-testid="beo-event-date" /></div>
      <div><Label>Event Time *</Label><Input type="time" value={bf.formData.event_time} onChange={(e) => bf.setFormData({ ...bf.formData, event_time: e.target.value })} /></div>
      <div><Label>Guest Count *</Label><Input type="number" value={bf.formData.guest_count} onChange={(e) => bf.setFormData({ ...bf.formData, guest_count: e.target.value })} data-testid="beo-guest-count" /></div>
    </div>
    <div className="grid grid-cols-2 gap-4 mt-4">
      <div><Label>Location *</Label><Input value={bf.formData.location} onChange={(e) => bf.setFormData({ ...bf.formData, location: e.target.value })} placeholder="Event venue address" /></div>
      <div>
        <Label>Room</Label>
        <Select value={bf.formData.room || ''} onValueChange={(v) => bf.setFormData({ ...bf.formData, room: v })}>
          <SelectTrigger data-testid="beo-room-select"><SelectValue placeholder="Select room" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pub">Pub</SelectItem><SelectItem value="banquet_hall">Banquet Hall</SelectItem>
            <SelectItem value="lower_patio">Lower Patio</SelectItem><SelectItem value="upper_patio">Upper Patio</SelectItem>
            <SelectItem value="ceremony_space">Ceremony Space</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  </div>
);

const EventTimelineSection = ({ bf }) => (
  <div className="border rounded-lg p-4">
    <h3 className="font-semibold text-[#1e3a5f] mb-4 flex items-center gap-2"><Clock size={18} /> Event Timeline</h3>
    <div className="grid grid-cols-4 gap-4">
      <div><Label>Event Start</Label><Input type="time" value={bf.formData.event_start_time} onChange={(e) => bf.setFormData({ ...bf.formData, event_start_time: e.target.value })} /></div>
      <div><Label>Cocktail Hour</Label><Input type="time" value={bf.formData.cocktail_hour_time} onChange={(e) => bf.setFormData({ ...bf.formData, cocktail_hour_time: e.target.value })} /></div>
      <div><Label>Dinner Start</Label><Input type="time" value={bf.formData.dinner_start_time} onChange={(e) => bf.setFormData({ ...bf.formData, dinner_start_time: e.target.value })} /></div>
      <div><Label>Event End</Label><Input type="time" value={bf.formData.event_end_time} onChange={(e) => bf.setFormData({ ...bf.formData, event_end_time: e.target.value })} /></div>
    </div>
  </div>
);

const MenuSelectionSection = ({ bf }) => (
  <div className="border rounded-lg p-4">
    <h3 className="font-semibold text-[#1e3a5f] mb-4 flex items-center gap-2"><Utensils size={18} /> Menu Selection</h3>
    <div className="grid grid-cols-2 gap-4 mb-4">
      <div>
        <Label>Service Style</Label>
        <Select value={bf.formData.service_style} onValueChange={(v) => bf.setFormData({ ...bf.formData, service_style: v })}>
          <SelectTrigger data-testid="beo-service-style"><SelectValue placeholder="Select style..." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="buffet">Buffet</SelectItem><SelectItem value="plated">Plated</SelectItem>
            <SelectItem value="family_style">Family Style</SelectItem><SelectItem value="stations">Food Stations</SelectItem>
            <SelectItem value="stationed_reception">Stationed Reception</SelectItem><SelectItem value="combination">Combination</SelectItem>
            <SelectItem value="cocktail_coach">Cocktail Coach</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Bar Type</Label>
        <Select value={bf.formData.bar_type} onValueChange={(v) => bf.setFormData({ ...bf.formData, bar_type: v })}>
          <SelectTrigger><SelectValue placeholder="Select bar type..." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="host_bar">Host Bar</SelectItem><SelectItem value="cash_bar">Cash Bar</SelectItem>
            <SelectItem value="toonie_bar">Toonie Bar</SelectItem><SelectItem value="ticket_bar">Ticket Bar</SelectItem>
            <SelectItem value="no_bar">No Bar</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <Label className="mb-2 block">Select Menus</Label>
    <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto border rounded p-2 bg-gray-50">
      {bf.menus.filter(m => m.event_type === bf.formData.event_type || bf.formData.event_type === 'offsite').map(menu => (
        <div key={menu.id} className="flex items-center gap-2 p-2 bg-white rounded border">
          <Checkbox id={`menu-${menu.id}`} checked={bf.formData.selected_menus.includes(menu.id)}
            onCheckedChange={(checked) => {
              if (checked) bf.setFormData({ ...bf.formData, selected_menus: [...bf.formData.selected_menus, menu.id] });
              else bf.setFormData({ ...bf.formData, selected_menus: bf.formData.selected_menus.filter(id => id !== menu.id) });
            }} />
          <label htmlFor={`menu-${menu.id}`} className="text-sm cursor-pointer flex-1">
            <span className="font-medium">{menu.name}</span>
            {menu.price_per_person && <span className="text-gray-500 ml-1">${menu.price_per_person}/pp</span>}
          </label>
        </div>
      ))}
    </div>
    {bf.formData.selected_menus.length > 0 && <p className="text-sm text-green-600 mt-2">{bf.formData.selected_menus.length} menu(s) selected</p>}
    <div className="mt-4 space-y-3">
      <Label className="text-sm font-semibold text-[#1e3a5f]">Custom Menus by Service Phase</Label>
      <BEOCustomMenuPhase bf={bf} label="Breakfast" textKey="custom_menu_breakfast" priceKey="custom_menu_breakfast_price" tiersKey="custom_menu_breakfast_tiers" textTestId="beo-custom-menu-breakfast" priceTestId="beo-custom-menu-breakfast-price" tierTestIdPrefix="beo-custom-menu-breakfast-tier" placeholder="Custom breakfast items, e.g. Pancakes, Eggs Benedict" />
      <BEOCustomMenuPhase bf={bf} label="Lunch" textKey="custom_menu_lunch" priceKey="custom_menu_lunch_price" tiersKey="custom_menu_lunch_tiers" textTestId="beo-custom-menu-lunch" priceTestId="beo-custom-menu-lunch-price" tierTestIdPrefix="beo-custom-menu-lunch-tier" placeholder="Custom lunch items, e.g. Caesar Salad, Sandwich Platter" />
      <BEOCustomMenuPhase bf={bf} label="Cocktail Hour / Hors d'Oeuvres" textKey="custom_menu_cocktail" priceKey="custom_menu_cocktail_price" tiersKey="custom_menu_cocktail_tiers" textTestId="beo-custom-menu-cocktail" priceTestId="beo-custom-menu-cocktail-price" tierTestIdPrefix="beo-custom-menu-cocktail-tier" placeholder="Custom cocktail hour items, e.g. Bruschetta, Caprese Skewers" />
      <BEOCustomMenuPhase bf={bf} label="Dinner Service" textKey="custom_menu_dinner" priceKey="custom_menu_dinner_price" tiersKey="custom_menu_dinner_tiers" textTestId="beo-custom-menu-dinner" priceTestId="beo-custom-menu-dinner-price" tierTestIdPrefix="beo-custom-menu-dinner-tier" placeholder="Custom dinner items, e.g. Roast Beef au Jus, Garlic Mashed" />
      <BEOCustomMenuPhase bf={bf} label="Dessert" textKey="custom_menu_dessert" priceKey="custom_menu_dessert_price" tiersKey="custom_menu_dessert_tiers" textTestId="beo-custom-menu-dessert" priceTestId="beo-custom-menu-dessert-price" tierTestIdPrefix="beo-custom-menu-dessert-tier" placeholder="Custom dessert items, e.g. Tiramisu, Fresh Fruit Platter" />
      <BEOCustomMenuPhase bf={bf} label="Late Night" textKey="custom_menu_late_night" priceKey="custom_menu_late_night_price" tiersKey="custom_menu_late_night_tiers" textTestId="beo-custom-menu-late-night" priceTestId="beo-custom-menu-late-night-price" tierTestIdPrefix="beo-custom-menu-late-night-tier" placeholder="Custom late night items, e.g. Slider Bar, Poutine Station" />
      <BEOCustomMenuPhase bf={bf} label="Bar" textKey="custom_menu_bar" priceKey="custom_menu_bar_price" tiersKey="custom_menu_bar_tiers" textTestId="beo-custom-menu-bar" priceTestId="beo-custom-menu-bar-price" tierTestIdPrefix="beo-custom-menu-bar-tier" placeholder="Custom bar package, e.g. Premium Open Bar, Signature Cocktails" />
      <BEOCustomMenuPhase bf={bf} label="Beverage" textKey="custom_menu_beverage" priceKey="custom_menu_beverage_price" tiersKey="custom_menu_beverage_tiers" textTestId="beo-custom-menu-beverage" priceTestId="beo-custom-menu-beverage-price" tierTestIdPrefix="beo-custom-menu-beverage-tier" placeholder="Custom beverage items, e.g. Coffee/Tea Service, Juice Bar" />
      <BEOCustomMenuPhase bf={bf} label="On the Turn" textKey="custom_menu_on_the_turn" priceKey="custom_menu_on_the_turn_price" tiersKey="custom_menu_on_the_turn_tiers" textTestId="beo-custom-menu-on-the-turn" priceTestId="beo-custom-menu-on-the-turn-price" tierTestIdPrefix="beo-custom-menu-on-the-turn-tier" placeholder="Custom on-the-turn items, e.g. Turn Dogs, Halfway House Snacks" />
      <details className="text-xs">
        <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Legacy: General custom menu notes</summary>
        <Textarea value={bf.formData.custom_menu} onChange={(e) => bf.setFormData({ ...bf.formData, custom_menu: e.target.value })} placeholder="Add custom menu notes, substitutions, or special items..." rows={3} className="font-mono text-sm mt-2" data-testid="beo-custom-menu" />
      </details>
    </div>
  </div>
);

/* Per-phase custom menu (text + price + tiers) for Standalone BEO */
const BEOCustomMenuPhase = ({ bf, label, textKey, priceKey, tiersKey, textTestId, priceTestId, tierTestIdPrefix, placeholder }) => {
  const guests = parseInt(bf.formData.guest_count) || 0;
  const tiers = bf.formData[tiersKey] || [];
  const validTiers = tiers.filter(t => (parseInt(t.quantity) || 0) > 0 && (parseFloat(t.price_per_person) || 0) > 0);
  const tierTotal = validTiers.reduce((s, t) => s + (parseInt(t.quantity) || 0) * (parseFloat(t.price_per_person) || 0), 0);
  const fallbackPrice = parseFloat(bf.formData[priceKey]) || 0;
  const fallbackTotal = fallbackPrice * guests;
  const usingTiers = validTiers.length > 0;
  const total = usingTiers ? tierTotal : fallbackTotal;
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <Label className="text-xs text-[#1e3a5f] font-semibold">{label}</Label>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-gray-500">$</span>
          <input
            type="number" min="0" step="0.01"
            data-testid={priceTestId}
            value={bf.formData[priceKey] ?? ''}
            onChange={(e) => bf.setFormData({ ...bf.formData, [priceKey]: e.target.value })}
            placeholder="0.00"
            disabled={usingTiers}
            className={`w-20 px-2 py-1 text-xs border rounded text-right ${usingTiers ? 'bg-gray-100 text-gray-400' : ''}`}
            title={usingTiers ? 'Disabled — using tier pricing below' : 'Flat per-person price (used when no tiers)'}
          />
          <span className="text-gray-500 whitespace-nowrap">/ person</span>
          {total > 0 && (
            <span className="ml-2 text-[#1e3a5f] font-semibold whitespace-nowrap">= ${total.toFixed(2)}</span>
          )}
        </div>
      </div>
      <Textarea
        data-testid={textTestId}
        value={bf.formData[textKey] || ''}
        onChange={(e) => bf.setFormData({ ...bf.formData, [textKey]: e.target.value })}
        placeholder={placeholder}
        rows={3}
        className="font-mono text-sm mt-1"
      />
      <CustomMenuTiers
        tiers={tiers}
        onChange={(next) => bf.setFormData({ ...bf.formData, [tiersKey]: next })}
        testIdPrefix={tierTestIdPrefix}
      />
    </div>
  );
};

const PlatedItemRow = ({ item, category, bf }) => (
  <div className="flex items-center justify-between p-2 border rounded hover:bg-gray-50">
    <div className="flex-1">
      <span className="text-sm font-medium">{item.name}</span>
      {item.sub_category && <span className="text-xs text-gray-500 ml-1">({item.sub_category})</span>}
      {item.description && <p className="text-xs text-gray-500 truncate">{item.description}</p>}
    </div>
    <div className="flex items-center gap-1">
      <Button type="button" variant="outline" size="sm" className="h-7 w-7 p-0"
        onClick={() => bf.handlePlatedQuantityChange(category, item.id, item.name, (bf.platedSelections[category][item.id]?.quantity || 0) - 1, item.description)}
        disabled={!bf.platedSelections[category][item.id]?.quantity}>
        <Minus size={14} />
      </Button>
      <Input type="number" min="0" value={bf.platedSelections[category][item.id]?.quantity || ''}
        onChange={(e) => bf.handlePlatedQuantityChange(category, item.id, item.name, parseInt(e.target.value) || 0, item.description)}
        className="w-14 h-7 text-center text-sm" placeholder="0" />
      <Button type="button" variant="outline" size="sm" className="h-7 w-7 p-0"
        onClick={() => bf.handlePlatedQuantityChange(category, item.id, item.name, (bf.platedSelections[category][item.id]?.quantity || 0) + 1, item.description)}>
        <Plus size={14} />
      </Button>
    </div>
  </div>
);

const PlatedMenuSection = ({ bf }) => (
  <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
    <h3 className="font-semibold text-amber-800 mb-4 flex items-center gap-2"><Utensils size={18} /> Plated Menu Selection</h3>
    <p className="text-sm text-amber-700 mb-4">Select items and specify quantities for each variety. Guest count: <strong>{bf.formData.guest_count || '0'}</strong></p>

    <PlatedCategory title="Starters (Salads & Appetizers)" category="starters" items={bf.starterItems} bf={bf} showGuestValidation={false} />
    <PlatedCategory title="Main Courses" category="mains" items={bf.mainItems} bf={bf} showGuestValidation={true} />
    <PlatedCategory title="Desserts" category="desserts" items={bf.dessertItems} bf={bf} showGuestValidation={true} />

    {bf.beverageItems.length > 0 && (
      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-semibold text-gray-700">Coffee & Tea Service</h4>
          <span className="text-sm bg-amber-100 px-2 py-1 rounded">Total: {bf.getCategoryTotal('beverages')} selected</span>
        </div>
        <div className="grid grid-cols-1 gap-2 bg-white rounded p-2 border">
          {bf.beverageItems.map(item => <PlatedItemRow key={item.id} item={item} category="beverages" bf={bf} />)}
        </div>
      </div>
    )}

    <PlatedSummary bf={bf} />
  </div>
);

const PlatedCategory = ({ title, category, items, bf, showGuestValidation }) => {
  const total = bf.getCategoryTotal(category);
  const guestCount = parseInt(bf.formData.guest_count || 0);
  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-3">
        <h4 className="font-semibold text-gray-700">{title}</h4>
        <span className={`text-sm px-2 py-1 rounded ${showGuestValidation ? (total === guestCount ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700') : 'bg-amber-100'}`}>
          Total: {total}{showGuestValidation ? ` / ${guestCount} guests` : ' selected'}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto bg-white rounded p-2 border">
        {items.map(item => <PlatedItemRow key={item.id} item={item} category={category} bf={bf} />)}
        {items.length === 0 && <p className="text-sm text-gray-500 col-span-2 text-center py-4">No items available</p>}
      </div>
    </div>
  );
};

const PlatedSummary = ({ bf }) => {
  const hasSelections = bf.getCategoryTotal('starters') > 0 || bf.getCategoryTotal('mains') > 0 || bf.getCategoryTotal('desserts') > 0 || bf.getCategoryTotal('beverages') > 0;
  if (!hasSelections) return null;
  return (
    <div className="mt-4 p-3 bg-white rounded-lg border">
      <h4 className="font-semibold text-gray-700 mb-2">Selection Summary</h4>
      <div className="text-sm space-y-1">
        {['starters', 'mains', 'desserts', 'beverages'].map(cat =>
          Object.entries(bf.platedSelections[cat]).filter(([_, v]) => v.quantity > 0).map(([id, item]) => (
            <div key={id} className="text-gray-600">{item.quantity}x {item.name} ({cat.charAt(0).toUpperCase() + cat.slice(1, -1)})</div>
          ))
        )}
      </div>
    </div>
  );
};

const CocktailCoachSection = ({ bf }) => (
  <div className="border rounded-lg p-4 bg-teal-50 border-teal-200">
    <h3 className="font-semibold text-teal-800 mb-4 flex items-center gap-2">Cocktail Coach Package</h3>
    <div className="space-y-3">
      <div>
        <Label>Select Package *</Label>
        <Select value={bf.formData.cocktail_coach_package} onValueChange={(v) => bf.setFormData({ ...bf.formData, cocktail_coach_package: v })}>
          <SelectTrigger className="bg-white"><SelectValue placeholder="Select a package..." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="essentials">The Essentials Bar - $750 (up to 75 guests, 1 bartender)</SelectItem>
            <SelectItem value="signature">The Signature Bar Experience - $950 (up to 150 guests, 2 bartenders)</SelectItem>
            <SelectItem value="luxe">The Luxe Bar Experience - $1,250 (up to 200 guests, 3 bartenders)</SelectItem>
            <SelectItem value="custom">Custom Package</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {bf.formData.cocktail_coach_package && <PackageDetails bf={bf} />}
      <div className="grid grid-cols-2 gap-4 mt-3">
        <div><Label>Signature Cocktail 1</Label><Input value={bf.formData.signature_cocktail_1 || ''} onChange={(e) => bf.setFormData({ ...bf.formData, signature_cocktail_1: e.target.value })} placeholder="e.g., Margarita" /></div>
        <div><Label>Signature Cocktail 2</Label><Input value={bf.formData.signature_cocktail_2 || ''} onChange={(e) => bf.setFormData({ ...bf.formData, signature_cocktail_2: e.target.value })} placeholder="e.g., Mojito" /></div>
      </div>
    </div>
  </div>
);

const PackageDetails = ({ bf }) => {
  const pkg = bf.formData.cocktail_coach_package;
  const packages = {
    essentials: { title: 'The Essentials Bar - $750', items: ['Fully Licensed & Insured', '1 Professional Smart Serve Certified Bartender', 'Up to 75 guests | 6 hours service', 'Mobile Bar Setup with basic decor', 'Ice, Garnishes, Basic Mixers & Disposables', 'Domestic Beer, Wine & Mixed Drinks'] },
    signature: { title: 'The Signature Bar Experience - $950', items: ['Everything in Essentials, PLUS:', '2 Professional Bartenders', 'Up to 150 guests | 6 hours service', 'Premium Mobile Bar Setup with themed decor', 'Premium Mixers & Disposables', '2 Signature Cocktails of your choice', 'Personalized drink menu display'] },
    luxe: { title: 'The Luxe Bar Experience - $1,250', items: ['Everything in Signature, PLUS:', '3 Professional Bartenders', 'Up to 200 guests | 6 hours service', 'Themed Chalkboard Menu', 'Lighting & Outdoor Bar Seating', 'Additional Wine & Beer Station', 'Mocktail & Non-Alcoholic Station', 'Pre-Event Consultation & Menu Design'] }
  };

  return (
    <div className="bg-white rounded-lg p-3 border">
      {packages[pkg] ? (
        <div>
          <p className="font-semibold text-teal-700">{packages[pkg].title}</p>
          <ul className="text-sm text-gray-600 mt-2 space-y-1">
            {packages[pkg].items.map((item, i) => <li key={i}>- {item}</li>)}
          </ul>
        </div>
      ) : pkg === 'custom' ? (
        <div>
          <p className="font-semibold text-teal-700">Custom Package</p>
          <p className="text-sm text-gray-600 mt-2">Contact us for a customized quote based on your specific needs.</p>
          <div className="mt-3">
            <Label>Custom Package Details</Label>
            <Textarea value={bf.formData.custom_package_notes || ''} onChange={(e) => bf.setFormData({ ...bf.formData, custom_package_notes: e.target.value })} placeholder="Describe your custom package requirements..." rows={3} />
          </div>
        </div>
      ) : null}
    </div>
  );
};

const TableLinenSection = ({ bf }) => (
  <div className="border rounded-lg p-4">
    <h3 className="font-semibold text-[#1e3a5f] mb-4 flex items-center gap-2"><Table2 size={18} /> Table & Linen Settings</h3>
    <div className="grid grid-cols-3 gap-4">
      <div>
        <Label>Table Style</Label>
        <Select value={bf.formData.table_style} onValueChange={(v) => bf.setFormData({ ...bf.formData, table_style: v })}>
          <SelectTrigger data-testid="table-style-select"><SelectValue placeholder="Select..." /></SelectTrigger>
          <SelectContent>{tableStyles.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Tablecloth Color</Label>
        <Select value={bf.formData.tablecloth_color} onValueChange={(v) => bf.setFormData({ ...bf.formData, tablecloth_color: v })}>
          <SelectTrigger data-testid="tablecloth-color-select"><SelectValue placeholder="Select..." /></SelectTrigger>
          <SelectContent>{tableclothColors.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Napkin Color</Label>
        <Select value={bf.formData.napkin_color} onValueChange={(v) => bf.setFormData({ ...bf.formData, napkin_color: v })}>
          <SelectTrigger data-testid="napkin-color-select"><SelectValue placeholder="Select..." /></SelectTrigger>
          <SelectContent>{napkinColors.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  </div>
);

const StationsSection = ({ bf }) => (
  <div className="border rounded-lg p-4">
    <h3 className="font-semibold text-[#1e3a5f] mb-4 flex items-center gap-2"><Settings2 size={18} /> Stations & Setup Requirements</h3>
    <div className="grid grid-cols-4 gap-4">
      {[
        { key: 'head_table', label: 'Head Table' }, { key: 'dj_table', label: 'DJ Table' },
        { key: 'gift_table', label: 'Gift Table' }, { key: 'cake_table', label: 'Cake Table' },
        { key: 'registration_table', label: 'Registration Table' }, { key: 'dessert_station', label: 'Dessert Station' },
        { key: 'water_station', label: 'Water Station' },
        { key: 'coffee_station', label: 'Coffee Station' }, { key: 'bar_setup', label: 'Bar Setup' },
        { key: 'podium', label: 'Podium' }, { key: 'microphone', label: 'Microphone' },
      ].map(station => (
        <div key={station.key} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
          <Checkbox id={station.key} checked={bf.formData[station.key]} onCheckedChange={(checked) => bf.setFormData({ ...bf.formData, [station.key]: checked })} data-testid={`station-${station.key}`} />
          <label htmlFor={station.key} className="text-sm cursor-pointer">{station.label}</label>
        </div>
      ))}
    </div>
  </div>
);

const NotesSection = ({ bf }) => (
  <div className="border rounded-lg p-4">
    <h3 className="font-semibold text-[#1e3a5f] mb-4">Notes & Special Requests</h3>
    <div className="space-y-4">
      <div><Label>Special Requests</Label><Textarea value={bf.formData.special_requests} onChange={(e) => bf.setFormData({ ...bf.formData, special_requests: e.target.value })} placeholder="Dietary restrictions, allergies, special accommodations..." rows={2} /></div>
      <div><Label>General Notes</Label><Textarea value={bf.formData.notes} onChange={(e) => bf.setFormData({ ...bf.formData, notes: e.target.value })} placeholder="Setup notes, coordination details..." rows={2} /></div>
    </div>
  </div>
);

export default StandaloneBEOs;
